import React, { useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { getFontStatusColor, getViewStatusColor } from '../../utils/commonMethods';
import useResponsive from '../../hooks/useResponsive';
import { usePopover } from '../../contexts/PopoverContext';
import CommonPopover from '../CommonPopover';

export interface Event {
    remarks_status: number;
    from_time: string;
    to_time: string;
    bundle_status: boolean;
    bundle_count?: number;
    task_name: string;
    customer_name: string;
    project_name: string;
    project_number?: string;
    municipality?: string;
    task_id: string;
    card_length: number;
    left_space_count: number;
    is_static: number;
    remarks: string;
    medewerker_level: number;
    project_id: number;
    comments: string;
}

interface BundleTaskItemProps {
    event: Event;
    isStatic: boolean;
}

export const getTextContent = (event: Event, isStatic: boolean) => {
    let contentText = "";
    if (event.medewerker_level === 0) {
        contentText = event.from_time + " - " + event.to_time;
        if (event.bundle_status) {
            contentText += " " + event.bundle_count + " - ";
        }
        contentText += " " + event.task_name + " - ";
        contentText += (event.customer_name ? event.customer_name : " ") + " - ";
        contentText += event.project_name;
        if (event.project_number) {
            contentText += " - " + event.project_number;
        }
        if (isStatic === false) {
            contentText += " - " + (event.municipality ? event.municipality : " ");
        }
    } else {
        contentText = event.project_name;
        if (event.project_number) {
            contentText += " - " + event.project_number;
        }
        contentText += " - " + (event.customer_name ? event.customer_name : " ") + " - " + (event.municipality ? event.municipality : " ");
    }
    return contentText;
}


const BundleTaskItem: React.FC<BundleTaskItemProps> = ({ event, isStatic }) => {

    const { isMobile } = useResponsive()
    const { setPopover } = usePopover();

    const handleOpenNotes = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setPopover(String(event.project_id), e.currentTarget);
    };

    useEffect(() => {
    }, [event])

    return (
        isMobile ?
            <Box
                sx={{
                    backgroundColor: getViewStatusColor(event.remarks_status),
                    borderRadius: "4px",
                    display: 'flex',
                    flex: 1,
                    padding: 0.5,
                    marginX: 0.25,
                    height: "32px",
                    alignItems: "center",
                    overflow: 'hidden',
                    justifyContent: 'space-between',
                }}
            >

                <Typography sx={{
                    color: getFontStatusColor(event.remarks_status),
                    fontSize: "10px",
                    fontFamily: "Nunito Sans SemiBold",
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    flex: 1,
                }}
                    noWrap
                >
                    {getTextContent(event, isStatic)}
                </Typography>

                {event.comments && (
                    <>
                        <IconButton
                            size="small"
                            onClick={handleOpenNotes}
                            sx={{ color: "black", ml: 1 }}
                        >
                            <DescriptionIcon fontSize="small" />
                        </IconButton>
                        <CommonPopover id={String(event.project_id)} content={event.comments} />
                    </>
                )}
            </Box > :
            <Box
                sx={{
                    backgroundColor: getViewStatusColor(event.remarks_status),
                    borderRadius: "2px",
                    display: 'flex',
                    flex: 1,
                    padding: 0.5,
                    marginX: 0.25
                }}
            >
                {event.medewerker_level === 0 ?
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                    }}>
                        <Typography sx={{
                            color: getFontStatusColor(event.remarks_status),
                            marginRight: 1,
                            fontFamily: "Nunito Sans Regular700",
                            fontSize: "14px"
                        }}>
                            {event.from_time} - {event.to_time}
                        </Typography>
                        {event.bundle_status && (
                            <Typography sx={{
                                color: getFontStatusColor(event.remarks_status),
                                marginRight: 1,
                                fontFamily: "Nunito Sans Regular500",
                                fontSize: "14px"
                            }}>
                                {event.bundle_count} -
                            </Typography>
                        )}
                        <Typography sx={{
                            color: getFontStatusColor(event.remarks_status),
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            marginRight: 1,
                        }}>
                            {event.task_name} -
                        </Typography>
                        <Typography sx={{
                            color: getFontStatusColor(event.remarks_status),
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            marginRight: 1,
                        }}>
                            {event.customer_name} -
                        </Typography>
                        <Typography sx={{
                            color: getFontStatusColor(event.remarks_status),
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            marginRight: 1,
                        }}>
                            {event.project_name}
                            {event.project_number ? ' - ' : ''}
                        </Typography>
                        <Typography sx={{
                            color: getFontStatusColor(event.remarks_status),
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "14px",
                            marginRight: 1,
                        }}>
                            {event.project_number}
                            {isStatic === false ? ' - ' : ''}
                        </Typography>
                        {isStatic === false && (
                            <Typography sx={{
                                color: getFontStatusColor(event.remarks_status),
                                fontFamily: "Nunito Sans Regular500",
                                fontSize: "14px",
                                marginRight: 1,
                            }}>
                                {event.municipality}
                            </Typography>
                        )}
                    </Box> :
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: "center",
                        width: "100%",
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography sx={{
                                color: getFontStatusColor(event.remarks_status),
                                marginRight: 1,
                                fontFamily: "Nunito Sans Regular700",
                                fontSize: "14px"
                            }}>
                                {event.project_name}
                                {event.project_number ? ' - ' : ''}
                            </Typography>
                            <Typography sx={{
                                color: getFontStatusColor(event.remarks_status),
                                fontFamily: "Nunito Sans Regular500",
                                fontSize: "14px",
                                marginRight: 1,
                            }}>
                                {event.project_number}
                                {isStatic === false ? ' - ' : ''}
                            </Typography>
                            <Typography sx={{
                                color: getFontStatusColor(event.remarks_status),
                                fontFamily: "Nunito Sans Regular500",
                                fontSize: "14px",
                                marginRight: 1,
                            }}>
                                {event.customer_name} -
                            </Typography>
                            {isStatic === false && (
                                <Typography sx={{
                                    color: getFontStatusColor(event.remarks_status),
                                    fontFamily: "Nunito Sans Regular500",
                                    fontSize: "14px",
                                    marginRight: 1,
                                }}>
                                    {event.municipality}
                                </Typography>
                            )}
                        </Box>
                        {/* Notes Icon Button */}
                        {event.comments && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleOpenNotes}
                                    sx={{ ml: 'auto', color: "black" }}
                                >
                                    <DescriptionIcon fontSize="small" />
                                </IconButton>

                                <CommonPopover id={String(event.project_id)} content={event.comments} />
                            </>
                        )}
                    </Box>}
            </Box>
    );
};

export default BundleTaskItem;